import { CloseOutline18Icon } from "@vygruppen/spor-icon-react";
import { useInfrastructureEvent } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/hooks/useInfrastructureEvent";
import { useTabsForRole } from "features/CenterContent/RoleContent/useTabsForRole";
import { useAlternativeTransportData } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/utils";
import ExpandButton from "features/CenterContent/shared/Tabs/ExpandButton";
import { TabIdElement } from "features/CenterContent/shared/Tabs/TabIdElement";
import {
  ActiveTab,
  CloseTabs,
  InActiveTab,
  TabList,
  TabsContentWrapper,
  TabText,
} from "features/CenterContent/shared/Tabs/TabsStyle";
import {
  BackupBusTab,
  BusTab,
  DecisionSupportTab,
  ReserveBusTab,
  Tab,
  TrainTab,
  useTabsContext,
} from "features/CenterContent/shared/Tabs/useTabsContext";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Text } from "shared/components/Spor";
import { AlertsCounter } from "shared/components/dataDisplay/AlertsCounter";
import * as ROUTES from "shared/utils/routes";
import { useDropsRole } from "stores/useDropsRole";
import { useSidePanelsState } from "stores/useSidePanelsState";
import { useStretchBuilder } from "stores/useStretchBuilder";
import styled, { useTheme } from "styled-components";

const maxTabs = 5;
const lastTabs = 6;

export const ButtonTextContainer = styled.div`
  display: flex;
  align-items: center;
`;

type TabCenterProps = {
  isReserveBus?: boolean;
  isBackUpBus?: boolean;
};

const TabsCenter = ({
  isReserveBus = false,
  isBackUpBus = false,
}: TabCenterProps) => {
  const navigate = useNavigate();
  const {
    trainNumber,
    trainDate,
    tabIdParam,
    countryCode,
    vehicleUuid,
    infrastructureEventId,
    uuid: _uuid,
  } = useParams();

  // Map infrastructureEventId to incidentId for consistency
  const uuid = _uuid ?? infrastructureEventId;

  const hasParams = Boolean(trainDate && trainNumber && countryCode);
  const hasBusParams = !!vehicleUuid;
  const hasReserveBusParams = !!vehicleUuid;
  const hasInfrastructureEventParams = !!uuid;

  const { status, data } = useAlternativeTransportData(vehicleUuid);

  const shiftId =
    data?.alternativeTransportResponseVehicle?.workShiftPlan?.shiftId ?? "O";
  const trainId =
    data?.alternativeTransportResponseVehicle?.trainIds?.join(":") ?? "";

  const location = useLocation();

  const { vehicleTitle } = location.state || { vehicleTitle: "Undefined" };

  const vehicleTabName =
    isReserveBus || isBackUpBus ? `${vehicleTitle}` : `${shiftId}-${trainId}`;

  // Fetch infrastructure event data to get a stretch name
  const { data: infrastructureEventData, status: infrastructureEventStatus } =
    useInfrastructureEvent(hasInfrastructureEventParams && uuid ? [uuid] : []);

  const { role } = useDropsRole();

  const { t } = useTranslation();

  const { activeIdTabs, setActiveIdTabs } = useTabsContext();
  const storage = sessionStorage.getItem("openTabs");

  const {
    trainListOpen,
    setTrainListOpen,
    oploggTodoListOpen,
    setOploggTodoListOpen,
  } = useSidePanelsState();

  const isStretchBuilderMode = useStretchBuilder((state) =>
    state.isStretchBuilderMode(),
  );

  function arrSelectedVehicle(prevArr: Tab[], newTabs: Tab) {
    return [...prevArr, newTabs];
  }

  useEffect(() => {
    if (storage) {
      setActiveIdTabs(JSON.parse(storage));
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem("openTabs", JSON.stringify(activeIdTabs));
  }, [activeIdTabs]);

  useEffect(() => {
    const dontExist =
      activeIdTabs.filter(
        (it: Tab): it is TrainTab =>
          "trainNumber" in it &&
          it.trainNumber === trainNumber &&
          it.trainDate === trainDate &&
          it.countryCode === countryCode,
      ).length === 0;
    const notInStorage = storage
      ? JSON.parse(storage).filter(
          (it: Tab): it is TrainTab =>
            "trainNumber" in it &&
            it.trainNumber === trainNumber &&
            it.trainDate === trainDate &&
            it.countryCode === countryCode,
        ).length === 0
      : true;

    if (hasParams) {
      if (dontExist && notInStorage && activeIdTabs.length <= maxTabs) {
        setActiveIdTabs((prevArr: Tab[]) =>
          arrSelectedVehicle(prevArr, {
            type: "train",
            trainNumber,
            trainDate,
            countryCode,
            tabId: tabIdParam!,
          } as TrainTab),
        );
      }

      if (dontExist && notInStorage && activeIdTabs.length === lastTabs) {
        setActiveIdTabs((prevArr: Tab[]) => {
          prevArr.shift();
          return arrSelectedVehicle(prevArr, {
            type: "train",
            trainNumber,
            trainDate,
            countryCode,
            tabId: tabIdParam!,
          } as TrainTab);
        });
      }
    }
  }, [trainNumber, trainDate, countryCode]);

  const truncateTabsName = (tabName: string) =>
    tabName.length > 26 ? `${tabName.slice(0, 26)}...` : tabName;

  useEffect(() => {
    if (
      status === "success" &&
      !isBackUpBus &&
      !isReserveBus &&
      vehicleTabName !== undefined
    ) {
      const busIdFromUUID = truncateTabsName(vehicleTabName);

      const dontExist =
        activeIdTabs.filter(
          (it: Tab): it is BusTab => "busId" in it && it.busId === vehicleUuid,
        ).length === 0;
      const notInStorage = storage
        ? JSON.parse(storage).filter(
            (it: Tab): it is BusTab =>
              "busId" in it && it.busId === vehicleUuid,
          ).length === 0
        : true;

      if (hasBusParams) {
        if (dontExist && notInStorage && activeIdTabs.length <= maxTabs) {
          setActiveIdTabs((prevArr: Tab[]) =>
            arrSelectedVehicle(prevArr, {
              type: "bus",
              busId: vehicleUuid,
              tabId: busIdFromUUID,
            } satisfies BusTab),
          );
        }

        if (dontExist && notInStorage && activeIdTabs.length === lastTabs) {
          setActiveIdTabs((prevArr: Tab[]) => {
            prevArr.shift();
            return arrSelectedVehicle(prevArr, {
              type: "bus",
              busId: vehicleUuid,
              tabId: busIdFromUUID,
            } satisfies BusTab);
          });
        }
      }
    }
  }, [vehicleUuid, status]);

  useEffect(() => {
    if (
      status === "success" &&
      isReserveBus &&
      !isBackUpBus &&
      vehicleTabName !== undefined
    ) {
      const reserveBusIdFromUUID = truncateTabsName(vehicleTabName);

      const dontExist =
        activeIdTabs.filter(
          (it: Tab): it is ReserveBusTab =>
            "reserveBusId" in it && it.reserveBusId === vehicleUuid,
        ).length === 0;
      const notInStorage = storage
        ? JSON.parse(storage).filter(
            (it: Tab): it is BusTab =>
              "reserveBusId" in it && it.reserveBusId === vehicleUuid,
          ).length === 0
        : true;

      if (hasReserveBusParams) {
        if (dontExist && notInStorage && activeIdTabs.length <= maxTabs) {
          setActiveIdTabs((prevArr: Tab[]) =>
            arrSelectedVehicle(prevArr, {
              type: "reservebus",
              reserveBusId: vehicleUuid,
              reserveTabId: reserveBusIdFromUUID,
            } satisfies ReserveBusTab),
          );
        }

        if (dontExist && notInStorage && activeIdTabs.length === lastTabs) {
          setActiveIdTabs((prevArr: Tab[]) => {
            prevArr.shift();
            return arrSelectedVehicle(prevArr, {
              type: "reservebus",
              reserveBusId: vehicleUuid,
              reserveTabId: reserveBusIdFromUUID,
            } satisfies ReserveBusTab);
          });
        }
      }
    }
  }, [vehicleUuid, status, isReserveBus]);

  useEffect(() => {
    if (
      status === "success" &&
      isBackUpBus &&
      !isReserveBus &&
      vehicleTabName !== undefined
    ) {
      const backupBusTruncatedName = truncateTabsName(vehicleTabName);

      const dontExist =
        activeIdTabs.filter(
          (it: Tab): it is BackupBusTab =>
            "backupBusId" in it && it.backupBusId === vehicleUuid,
        ).length === 0;
      const notInStorage = storage
        ? JSON.parse(storage).filter(
            (it: Tab): it is BusTab =>
              "backupBusId" in it && it.backupBusId === vehicleUuid,
          ).length === 0
        : true;

      if (hasReserveBusParams) {
        if (dontExist && notInStorage && activeIdTabs.length <= maxTabs) {
          setActiveIdTabs((prevArr: Tab[]) =>
            arrSelectedVehicle(prevArr, {
              type: "backupbus",
              backupBusId: vehicleUuid,
              backupTabId: backupBusTruncatedName,
            } satisfies BackupBusTab),
          );
        }

        if (dontExist && notInStorage && activeIdTabs.length === lastTabs) {
          setActiveIdTabs((prevArr: Tab[]) => {
            prevArr.shift();
            return arrSelectedVehicle(prevArr, {
              type: "backupbus",
              backupBusId: vehicleUuid,
              backupTabId: backupBusTruncatedName,
            } satisfies BackupBusTab);
          });
        }
      }
    }
  }, [vehicleUuid, status, isBackUpBus]);

  useEffect(() => {
    if (
      hasInfrastructureEventParams &&
      infrastructureEventStatus === "success" &&
      infrastructureEventData?.[0]
    ) {
      const { stretchName } = infrastructureEventData[0];
      const newTab = {
        type: "decision-support",
        uuid,
        stretchName,
      } satisfies DecisionSupportTab;

      setActiveIdTabs((_prevArr: Tab[]) => {
        // TODO: remove
        const hasOldTabs = _prevArr.some(
          (tab) => tab.type === "infrastructureEvent",
        );
        const prevArr = hasOldTabs
          ? _prevArr.map((tab) =>
              tab.type === "infrastructureEvent"
                ? ({
                    type: "decision-support",
                    uuid: tab.incidentId,
                    stretchName: tab.stretchName,
                  } satisfies DecisionSupportTab)
                : tab,
            )
          : _prevArr;

        const eventAlreadyOpen = prevArr.some(
          (it: Tab): it is DecisionSupportTab =>
            it.type === "decision-support" && it.uuid === uuid,
        );

        if (eventAlreadyOpen) {
          return prevArr;
        }

        if (prevArr.length === lastTabs) {
          return arrSelectedVehicle(prevArr.slice(1), newTab);
        }

        if (prevArr.length <= maxTabs) {
          return arrSelectedVehicle(prevArr, newTab);
        }

        return prevArr;
      });
    }
  }, [
    infrastructureEventId,
    hasInfrastructureEventParams,
    uuid,
    infrastructureEventStatus,
    infrastructureEventData,
  ]);

  const theme = useTheme();

  const TabsForAGivenRole = useTabsForRole(role);

  return (
    <TabsContentWrapper $hideTabs={isStretchBuilderMode}>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "end",
        }}
      >
        {/* ExpandButton for TrainList */}
        <ExpandButton
          label={t("Togliste")}
          open={trainListOpen}
          setOpen={setTrainListOpen}
        />
        <TabList style={{ flexGrow: 1 }}>
          {activeIdTabs.map((Tabs: Tab, index: number) => {
            let key: string;
            if (Tabs.type === "train") {
              key = `${Tabs.trainNumber}-${Tabs.trainDate}-${Tabs.countryCode}`;
            } else if (Tabs.type === "bus") {
              key = Tabs.busId;
            } else if (Tabs.type === "infrastructureEvent") {
              key = Tabs.incidentId;
            } else if (Tabs.type === "decision-support") {
              key = Tabs.uuid;
            } else if (Tabs.type === "reservebus") {
              key = Tabs.reserveBusId;
            } else if (Tabs.type === "backupbus") {
              key = Tabs.backupBusId;
            } else {
              key = index.toString();
            }
            return <TabIdElement key={key} tab={Tabs} index={index} />;
          })}
          {activeIdTabs.length > 1 && (
            <CloseTabs
              title={t("Lukk alle")}
              onClick={() => {
                sessionStorage.removeItem("openTabs");
                setActiveIdTabs([]);
                void navigate(ROUTES.DASHBOARD);
              }}
            >
              <ButtonTextContainer>
                <Text variant="sm" margin="auto" style={{ lineHeight: 0 }}>
                  {t("Lukk alle")}
                </Text>
                <CloseOutline18Icon
                  marginLeft="4px"
                  color={theme.colorTextMain}
                />
              </ButtonTextContainer>
            </CloseTabs>
          )}
        </TabList>
        <TabList>
          {TabsForAGivenRole?.map((it) => {
            const isActive = !hasParams && tabIdParam === it.id;
            const TabsForRoleItem = isActive ? ActiveTab : InActiveTab;
            const icon = isActive ? it.iconActive : it.iconInactive;
            return (
              <TabsForRoleItem
                key={it.id}
                title={`${t("Vis")} ${it.name}`}
                onClick={() => {
                  void navigate(
                    generatePath(ROUTES.LANDING, {
                      roleIdParam: role,
                      tabIdParam: it.id,
                    }),
                  );
                }}
              >
                <TabText as="div">
                  {icon}
                  <Text mt="2px">{it.name}</Text>
                  {it.alertCount && (
                    <AlertsCounter
                      alerts={it.alertCount}
                      semantics={it.alertSemantics ?? "alarm"}
                    />
                  )}
                </TabText>
              </TabsForRoleItem>
            );
          })}
        </TabList>
        <ExpandButton
          label="Oplogg"
          open={oploggTodoListOpen}
          setOpen={setOploggTodoListOpen}
          inverted
        />
      </div>
    </TabsContentWrapper>
  );
};

export default TabsCenter;
